import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  effect,
  inject,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, TabService } from '@core';

@Component({
  selector: 'app-tab-bar',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './tab-bar.component.html',
})
export class TabBarComponent implements AfterViewInit {
  readonly tabService = inject(TabService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);

  readonly toggleDrawer = output<void>();
  readonly profileOpen = signal(false);
  readonly contextMenu = signal<{ tabId: string; x: number; y: number } | null>(null);

  readonly tabsScrollEl = viewChild<ElementRef<HTMLElement>>('tabsScroll');
  readonly canScrollLeft = signal(false);
  readonly canScrollRight = signal(false);

  private readonly defaultRoute = '/dashboard';

  constructor() {
    effect(() => {
      this.tabService.tabs();
      setTimeout(() => this.checkScrollState(), 50);
    });
  }

  ngAfterViewInit(): void {
    const el = this.tabsScrollEl()?.nativeElement;
    if (!el) return;
    this.checkScrollState();
    new ResizeObserver(() => {
      this.ngZone.run(() => this.checkScrollState());
    }).observe(el);
  }

  onTabScroll(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLElement) {
      this.updateScrollState(target);
    }
  }

  checkScrollState(): void {
    const el = this.tabsScrollEl()?.nativeElement;
    if (el) this.updateScrollState(el);
  }

  updateScrollState(el: HTMLElement): void {
    this.canScrollLeft.set(el.scrollLeft > 2);
    this.canScrollRight.set(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }

  scrollTabsBy(delta: number): void {
    const el = this.tabsScrollEl()?.nativeElement;
    if (el) el.scrollBy({ left: delta, behavior: 'smooth' });
  }

  scrollActiveTabIntoView(): void {
    setTimeout(() => {
      const el = this.tabsScrollEl()?.nativeElement;
      if (!el) return;
      const activeEl = el.querySelector('[data-active="true"]');
      if (activeEl instanceof HTMLElement) {
        activeEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      this.checkScrollState();
    }, 60);
  }

  activate(id: string, route: string): void {
    this.tabService.activateTab(id);
    this.router.navigate([route]);
    this.scrollActiveTabIntoView();
  }

  onTabKeydown(event: KeyboardEvent, id: string, route: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.activate(id, route);
    }
  }

  close(id: string, event: MouseEvent): void {
    event.stopPropagation();
    const nextRoute = this.tabService.closeTab(id);
    if (nextRoute) this.router.navigate([nextRoute]);
    else this.router.navigate([this.defaultRoute]);
  }

  onTabRightClick(tabId: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenu.set({ tabId, x: event.clientX, y: event.clientY });
    this.profileOpen.set(false);
  }

  dismissContextMenu(): void {
    this.contextMenu.set(null);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.contextMenu()) this.dismissContextMenu();
    if (this.profileOpen()) this.profileOpen.set(false);
  }

  ctxClose(tabId: string): void {
    this.dismissContextMenu();
    const nextRoute = this.tabService.closeTab(tabId);
    if (nextRoute) this.router.navigate([nextRoute]);
    else this.router.navigate([this.defaultRoute]);
  }

  ctxCloseOthers(tabId: string): void {
    this.dismissContextMenu();
    this.tabService.closeOtherTabs(tabId);
    const tab = this.tabService.tabs().find((t) => t.id === tabId);
    if (tab) this.router.navigate([tab.route]);
  }

  ctxCloseToRight(tabId: string): void {
    this.dismissContextMenu();
    const route = this.tabService.closeTabsToRight(tabId);
    if (route) this.router.navigate([route]);
  }

  ctxCloseAll(): void {
    this.dismissContextMenu();
    this.tabService.closeAllTabs();
    this.router.navigate([this.defaultRoute]);
  }

  navigateTo(route: string, id: string, title: string): void {
    this.tabService.openTab({ id, title, route });
    this.router.navigate([route]);
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.authService.removeTokens();
        this.router.navigate(['/auth/login']);
      },
      error: () => {
        this.authService.removeTokens();
        this.router.navigate(['/auth/login']);
      },
    });
  }

  userDisplayName(): string {
    return 'Admin';
  }

  userInitials(): string {
    return 'AD';
  }

  userSubtitle(): string {
    return 'Administrator';
  }

  readonly profileMenuItems: Array<{
    label: string;
    icon: string;
    divider?: boolean;
    danger?: boolean;
    action: () => void;
  }> = [
    {
      label: 'Sign Out',
      icon: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1',
      danger: true,
      action: () => this.logout(),
    },
  ];
}
